import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Users, Building2, ExternalLink, LayoutGrid, List, Landmark } from "lucide-react";


const SOURCE_LABELS = {
  client_request: "Solicitada pelo cliente",
  manual_admin: "Direcionada pelo admin",
  auto_critical: "Destaque automático antigo"
};

const AGE_COLOR_CLASSES = {
  green: "border-l-emerald-500 bg-emerald-50/40",
  yellow: "border-l-amber-500 bg-amber-50/50",
  red: "border-l-rose-500 bg-rose-50/60",
  slate: "border-l-slate-300 bg-slate-50"
};

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

const getAgingMeta = (referral) => {
  const days = Number(referral?.status_age_days || 0);
  const colorKey = referral?.status_age_color || "green";
  const label = days > 0 ? `${days} dia${days > 1 ? "s" : ""} nesta etapa` : "Movimentado hoje";
  return {
    label,
    className: AGE_COLOR_CLASSES[colorKey] || AGE_COLOR_CLASSES.green,
    badgeClass:
      colorKey === "red"
        ? "bg-rose-100 text-rose-800"
        : colorKey === "yellow"
        ? "bg-amber-100 text-amber-800"
        : colorKey === "slate"
        ? "bg-slate-100 text-slate-700"
        : "bg-emerald-100 text-emerald-800",
  };
};

const STATUS_OPTIONS = [
  { value: "new", label: "Novo", color: "bg-blue-100 text-blue-800" },
  { value: "assigned", label: "Atribuído", color: "bg-amber-100 text-amber-800" },
  { value: "contacted", label: "Contactado", color: "bg-purple-100 text-purple-800" },
  { value: "closed", label: "Fechado", color: "bg-emerald-100 text-emerald-800" },
  { value: "not_closed_contract", label: "Não Fechou Contrato", color: "bg-rose-100 text-rose-800" }
];

const AdminReferrals = () => {
  const [referrals, setReferrals] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [updateData, setUpdateData] = useState({ consultant_id: "", status: "", notes_admin: "" });
  const [consultantCityFilter, setConsultantCityFilter] = useState("all");
  const [kanbanMode, setKanbanMode] = useState(() => localStorage.getItem("referralsViewMode") === "kanban");

  useEffect(() => {
    localStorage.setItem("referralsViewMode", kanbanMode ? "kanban" : "list");
  }, [kanbanMode]);

  const fetchData = useCallback(async () => {
    try {
      const statusParam = filterStatus !== "all" ? `?status=${filterStatus}` : "";
      const [referralsRes, consultantsRes] = await Promise.all([
        axios.get(`${API}/referrals${statusParam}`, { withCredentials: true }),
        axios.get(`${API}/consultants?active_only=true`, { withCredentials: true })
      ]);
      setReferrals(referralsRes.data.referrals || []);
      setConsultants(consultantsRes.data.consultants || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateReferral = async () => {
    if (!selectedReferral) return;

    try {
      const payload = {
        status: updateData.status,
        notes_admin: updateData.notes_admin
      };

      if (updateData.consultant_id && updateData.consultant_id !== "none") {
        payload.consultant_id = updateData.consultant_id;
      }

      await axios.put(`${API}/referrals/${selectedReferral.referral_id}`, payload, { withCredentials: true });
      toast.success("Encaminhamento atualizado!");
      setShowUpdateDialog(false);
      setSelectedReferral(null);
      fetchData();
    } catch (error) {
      console.error("Error updating referral:", error);
      toast.error("Erro ao atualizar");
    }
  };

  const openUpdateDialog = (referral) => {
    setSelectedReferral(referral);
    setUpdateData({
      consultant_id: referral.consultant_id || "none",
      status: referral.status,
      notes_admin: referral.notes_admin || ""
    });
    setConsultantCityFilter("all");
    setShowUpdateDialog(true);
  };

  const moveCardToStatus = async (referralId, targetStatus) => {
    try {
      await axios.put(`${API}/referrals/${referralId}`, { status: targetStatus }, { withCredentials: true });
      setReferrals((prev) => prev.map((r) => (r.referral_id === referralId ? { ...r, status: targetStatus } : r)));
      toast.success("Etapa atualizada");
    } catch (error) {
      console.error("Error moving card:", error);
      toast.error("Erro ao mover card");
    }
  };

  const onDragStart = (event, referralId) => {
    event.dataTransfer.setData("text/referralId", referralId);
  };

  const onDrop = (event, status) => {
    event.preventDefault();
    const referralId = event.dataTransfer.getData("text/referralId");
    if (referralId) {
      moveCardToStatus(referralId, status);
    }
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find((o) => o.value === status);
    return <Badge className={option?.color || "bg-slate-100"}>{option?.label || status}</Badge>;
  };

  const getScoreColor = (score) => {
    if (!score) return "text-slate-500";
    if (score >= 7) return "text-emerald-600";
    if (score >= 5) return "text-amber-600";
    return "text-red-600";
  };

  const getConsultantsForAreaAndCity = (area, city = "all") => {
    const normalize = (value) =>
      (value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();
    const normalizedArea = normalize(area);
    const normalizedCity = normalize(city);
    const areaConsultants = consultants.filter((c) =>
      (c.areas_supported || []).some((supportedArea) => normalize(supportedArea) === normalizedArea)
    );
    if (city === "all") return areaConsultants;
    if (city === "online") return areaConsultants.filter((c) => c.online_only);
    return areaConsultants.filter((c) => normalize(c.city) === normalizedCity);
  };

  const getAvailableCities = (area) => {
    const normalize = (value) =>
      (value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();
    const normalizedArea = normalize(area);
    const areaConsultants = consultants.filter((c) =>
      (c.areas_supported || []).some((supportedArea) => normalize(supportedArea) === normalizedArea)
    );
    const citySet = new Set(areaConsultants.map((c) => (c.city || "").trim()).filter(Boolean));
    const cities = Array.from(citySet).sort((a, b) => a.localeCompare(b, "pt-BR"));
    const hasOnlineOnly = areaConsultants.some((c) => c.online_only);
    if (hasOnlineOnly) {
      cities.push("Online");
    }
    return cities;
  };

  const renderCard = (referral, compact = false) => {
    const aging = getAgingMeta(referral);
    return (
      <div
        key={referral.referral_id}
        draggable
        onDragStart={(e) => onDragStart(e, referral.referral_id)}
        className={`border-l-4 border rounded p-3 ${compact ? "mb-3" : ""} cursor-move hover:shadow-sm transition ${aging.className}`}
        data-testid={`kanban-referral-${referral.referral_id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-medium text-navy text-sm truncate">{referral.company_name}</h3>
            <p className="text-xs text-slate-600 mt-1">
              Área: <span className="font-medium">{referral.area}</span>
              {referral.area_score != null && (
                <span className={`ml-2 font-mono font-bold ${getScoreColor(referral.area_score)}`}>
                  • {Number(referral.area_score).toFixed(2)}
                </span>
              )}
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              <Badge variant="outline">{SOURCE_LABELS[referral.source] || "Encaminhamento"}</Badge>
              {getStatusBadge(referral.status)}
              <Badge className={aging.badgeClass}>{aging.label}</Badge>
            </div>
            <p className="text-xs text-slate-500 mt-2">Criado em {formatDateTime(referral.created_at)}</p>
            <p className="text-xs text-slate-500">Entrou na etapa em {formatDateTime(referral.status_entered_at)}</p>
            {referral.consultant_name && <p className="text-xs text-slate-500 mt-1">Consultor: {referral.consultant_name}</p>}
            {referral.has_financial_assessment && (
              <p className="text-xs text-slate-500 inline-flex items-center gap-1 mt-1">
                <Landmark className="h-3 w-3" />
                Financeiro: {referral.financial_estado || "Indeterminado"}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-3">
          <Button variant="outline" size="sm" onClick={() => openUpdateDialog(referral)}>
            Gerenciar
          </Button>
          <Link to={`/admin/results/${referral.diagnostic_id}`}>
            <Button variant="ghost" size="sm">
              <ExternalLink className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-serif font-bold text-navy">Encaminhamentos</h1>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <List className="h-4 w-4 text-slate-500" />
              <Switch checked={kanbanMode} onCheckedChange={setKanbanMode} data-testid="kanban-mode-switch" />
              <LayoutGrid className="h-4 w-4 text-slate-500" />
              <span className="text-sm text-slate-600">Modo Kanban</span>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40" data-testid="filter-status-select">
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {referrals.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Nenhum encaminhamento encontrado.</p>
            </CardContent>
          </Card>
        ) : kanbanMode ? (
          <div className="overflow-x-auto pb-3">
            <div className="flex items-start gap-4 min-w-max">
              <div className="bg-sky-50 border border-sky-100 rounded p-3 min-h-[280px] w-[320px] shrink-0" data-testid="kanban-column-client-request">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-sm text-navy">Solicitadas pelo Cliente</span>
                  <Badge className="bg-sky-100 text-sky-800">{referrals.filter((r) => r.source === "client_request" && !["closed", "completed", "not_closed_contract"].includes(r.status)).length}</Badge>
                </div>
                {referrals.filter((r) => r.source === "client_request" && !["closed", "completed", "not_closed_contract"].includes(r.status)).length === 0 ? (
                  <p className="text-xs text-slate-400">Nenhum pedido ativo do cliente.</p>
                ) : (
                  referrals
                    .filter((r) => r.source === "client_request" && !["closed", "completed", "not_closed_contract"].includes(r.status))
                    .map((referral) => renderCard(referral, true))
                )}
              </div>
              {STATUS_OPTIONS.map((column) => {
                const columnItems = referrals.filter((r) => r.status === column.value);
                return (
                  <div
                    key={column.value}
                    className="bg-slate-50 border rounded p-3 min-h-[280px] w-[320px] shrink-0"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => onDrop(e, column.value)}
                    data-testid={`kanban-column-${column.value}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-sm text-navy">{column.label}</span>
                      <Badge className={column.color}>{columnItems.length}</Badge>
                    </div>
                    {columnItems.length === 0 ? (
                      <p className="text-xs text-slate-400">Arraste cards para esta etapa</p>
                    ) : (
                      columnItems.map((referral) => renderCard(referral, true))
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {referrals.map((referral) => (
              <Card key={referral.referral_id} className="card-hover">
                <CardContent className="py-4">{renderCard(referral)}</CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif">Gerenciar Encaminhamento</DialogTitle>
            </DialogHeader>
            {selectedReferral && (
              <div className="space-y-4 mt-4">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Empresa</p>
                  <p className="font-medium">{selectedReferral.company_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Área</p>
                  <p className="font-medium">{selectedReferral.area}</p>
                </div>

                <div className="space-y-2">
                  <Label>Cidade</Label>
                  <Select
                    value={consultantCityFilter}
                    onValueChange={(v) => {
                      setConsultantCityFilter(v);
                      setUpdateData({ ...updateData, consultant_id: "none" });
                    }}
                  >
                    <SelectTrigger data-testid="select-consultant-city">
                      <SelectValue placeholder="Filtrar por cidade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as cidades</SelectItem>
                      {getAvailableCities(selectedReferral.area).map((city) => (
                        <SelectItem key={city} value={city === "Online" ? "online" : city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Consultor</Label>
                  <Select value={updateData.consultant_id} onValueChange={(v) => setUpdateData({ ...updateData, consultant_id: v })}>
                    <SelectTrigger data-testid="select-consultant">
                      <SelectValue placeholder="Selecione um consultor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {getConsultantsForAreaAndCity(selectedReferral.area, consultantCityFilter).map((c) => (
                        <SelectItem key={c.consultant_id} value={c.consultant_id}>{c.user_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={updateData.status} onValueChange={(v) => setUpdateData({ ...updateData, status: v })}>
                    <SelectTrigger data-testid="select-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Observações Admin</Label>
                  <Textarea
                    value={updateData.notes_admin}
                    onChange={(e) => setUpdateData({ ...updateData, notes_admin: e.target.value })}
                    placeholder="Notas internas..."
                    data-testid="notes-admin-textarea"
                  />
                </div>

                <Button className="w-full bg-navy hover:bg-navy/90" onClick={handleUpdateReferral}>
                  Salvar Alterações
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminReferrals;
